/**
 * Single source of truth for who the shopper is, at runtime.
 *
 * WHY THIS EXISTS
 * Every B2B feature in this theme used to be gated server-side by
 * `{% if customer %}`: the scripts themselves were only emitted for signed-in
 * shoppers, and the customer's tags were baked into a handful of JSON blobs.
 * That has two failure modes, and the second one is the expensive one:
 *
 *   1. A document rendered logged out can never recover. The Section Rendering
 *      API only swaps section innerHTML, so it never re-injects the layout's
 *      <script> tags — the B2B scripts are simply absent from that document
 *      until a full reload. On mobile the back-forward cache serves exactly
 *      such a document whenever the shopper returns from the hosted login.
 *
 *   2. Section HTML that depends on `customer` is HTML we cannot trust. The
 *      Section Rendering API's customer context is undocumented and reported
 *      unreliable in the wild, so a re-rendered cart drawer could come back
 *      wearing the guest markup for a signed-in wholesale shopper — losing
 *      their prices and progress bar on an ordinary quantity change.
 *
 * The rule this module exists to enforce: NO Liquid inside a re-renderable
 * section may branch on `customer`. Sections render the same markup for
 * everyone, and every customer-dependent decision is made here, on the client,
 * from state that can be revalidated at any moment. Whether section rendering
 * exposes `customer` then stops mattering.
 *
 * SEEDING AND REVALIDATION
 * `window.themeB2BSessionSeed` is written into the layout head by Liquid, so
 * on a freshly rendered document the tags are correct during parsing — that is
 * what keeps `b2b-prices.js` free of its price flash. The seed is only ever a
 * starting point: on a bfcache restore and on every return to the tab, the
 * live state is fetched from sections/customer-state.liquid and compared.
 *
 * On a real change this both fixes the page in place (subscribers re-run
 * against the new tags, immediately) and schedules a reload (the rest of the
 * page — header account link, drawers, promos — is still server-gated chrome
 * this module does not own). The in-place fix is not redundant with the
 * reload: it is what keeps the shopper correct when the reload cannot happen,
 * because storage is blocked, the loop guard already fired, or they are
 * offline.
 */
(function () {
  'use strict';

  var PROBE_THROTTLE_MS = 10000;
  var RELOAD_FLAG = 'themeB2BSessionReloaded';
  var CHANGE_EVENT = 'theme:b2b:session-change';

  var seed = window.themeB2BSessionSeed || {};
  var checking = false;
  var lastCheckedAt = 0;

  function normalizeTags(tags) {
    return (tags || []).map(function (tag) {
      return String(tag).trim().toLowerCase();
    });
  }

  var state = {
    id: String(seed.id == null ? '' : seed.id),
    tags: normalizeTags(seed.tags)
  };

  var session = {
    /** Shopify customer id for this session, '' when signed out. */
    get id() {
      return state.id;
    },
    /** Customer tags, trimmed and lowercased. Empty for retail and guests. */
    get tags() {
      return state.tags.slice();
    },
    isLoggedIn: function () {
      return state.id !== '';
    },
    has: function (tag) {
      return state.tags.indexOf(String(tag).trim().toLowerCase()) !== -1;
    },
    /**
     * Subscribe to session changes. Returns an unsubscribe function. The
     * callback also fires for a sign-out, so subscribers must be able to undo
     * whatever they applied, not only apply it.
     */
    onChange: function (callback) {
      function handler(event) {
        callback(event.detail);
      }
      document.addEventListener(CHANGE_EVENT, handler);
      return function () {
        document.removeEventListener(CHANGE_EVENT, handler);
      };
    },
    refresh: function (options) {
      return checkSessionState(options || {});
    }
  };

  /* ------------------------------------------------------------ probing */

  function probeUrl() {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // `cache: no-store` alone is not enough for a plain storefront GET: without
    // the nonce an edge cache can hand back the guest render and quietly
    // defeat the entire check.
    return base + '?section_id=customer-state&_=' + Date.now();
  }

  function parseProbe(html) {
    var probe = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-customer-state]');
    if (!probe) return null;

    var rawTags = probe.getAttribute('data-customer-tags') || '';
    return {
      id: probe.getAttribute('data-customer-id') || '',
      tags: normalizeTags(rawTags ? rawTags.split(',') : [])
    };
  }

  function sameSession(a, b) {
    return a.id === b.id && a.tags.join('|') === b.tags.join('|');
  }

  /**
   * One reload per document. A probe that keeps disagreeing — an edge still
   * serving a stale guest response, say — would otherwise loop the page. The
   * in-place fix above has already run by this point, so a suppressed reload
   * degrades the page to "chrome is stale", not "B2B is broken".
   */
  function reloadOnce() {
    try {
      if (sessionStorage.getItem(RELOAD_FLAG) === window.location.href) return;
      sessionStorage.setItem(RELOAD_FLAG, window.location.href);
    } catch (error) {
      // Private mode or storage blocked: risk a second reload rather than
      // leave the shopper looking at the wrong prices.
    }
    window.location.reload();
  }

  function applySession(next) {
    var previous = { id: state.id, tags: state.tags.slice() };
    state.id = next.id;
    state.tags = next.tags;

    syncGuestOnly();
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { previous: previous, current: next } })
    );
  }

  function checkSessionState(options) {
    var now = Date.now();
    if (checking) return Promise.resolve(false);
    if (!options.force && now - lastCheckedAt < PROBE_THROTTLE_MS) return Promise.resolve(false);

    checking = true;
    lastCheckedAt = now;

    return fetch(probeUrl(), { cache: 'no-store', credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (html) {
        if (!html) return false;
        var next = parseProbe(html);
        if (!next || sameSession(state, next)) return false;

        applySession(next);
        reloadOnce();
        return true;
      })
      .catch(function () {
        // Offline or blocked: the seeded state stays in force.
        return false;
      })
      .then(function (changed) {
        checking = false;
        return changed;
      });
  }

  /* ------------------------------------------------- server-gated markup */

  /**
   * The cart's "log in" call to action is the one piece of guest-only markup
   * that lives inside a re-rendered section, so Liquid renders it hidden for
   * everyone and this decides. Hidden is the safe default: a guest seeing the
   * button a frame late costs nothing, a signed-in wholesale shopper being
   * told to log in is the bug we are fixing.
   */
  function syncGuestOnly() {
    var loggedIn = session.isLoggedIn();
    document.querySelectorAll('[data-b2b-guest-only]').forEach(function (el) {
      el.hidden = loggedIn;
    });
  }

  /* -------------------------------------------------------------- wiring */

  function watch() {
    syncGuestOnly();

    // Sections re-render over AJAX and bring fresh guest-only markup with them.
    var observer = new MutationObserver(function (mutations) {
      var needsSync = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return (
            node.nodeType === 1 &&
            (node.hasAttribute('data-b2b-guest-only') ||
              node.querySelector('[data-b2b-guest-only]'))
          );
        });
      });
      if (needsSync) syncGuestOnly();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // A bfcache restore is stale by definition, so it skips the throttle.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) checkSessionState({ force: true });
    });

    // Covers signing in from a second tab and coming back to this one.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') checkSessionState({});
    });
  }

  /*
   * TRUST BOUNDARY — read this before wiring anything new into the session.
   *
   * Nothing decided here is a security control. The storefront is the
   * shopper's own machine: any script on the page, any browser extension and
   * any devtools console can rewrite what these tags say, and therefore what
   * price the theme paints. What the shopper is actually charged is settled
   * elsewhere — the BSS B2B discount is applied at checkout against the real
   * server-side customer record, and Shopify prices the order from its own
   * data. This module exists so the shopper is SHOWN the right number, never
   * so they are CHARGED it.
   *
   * The practical rule for anyone extending this: never let a discount, a tier
   * or an eligibility decision exist only on the client. Read the tags here to
   * render; let the app and checkout decide what is owed. If a future feature
   * needs a price the customer must be held to, it belongs in a server-side
   * discount or a draft order, not in this file.
   *
   * The object is frozen and exposed as a non-writable, non-configurable
   * property so a later script — ours, an app block, a pasted snippet — cannot
   * quietly swap in its own session and have the rest of the theme believe it.
   * That is hygiene against accident and sloppiness, not a defence against an
   * attacker who already controls the page. There is no such defence here.
   */
  Object.freeze(session);
  Object.defineProperty(window, 'themeB2BSession', {
    value: session,
    writable: false,
    configurable: false,
    enumerable: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
